import React from "react";
import { SkeletonBlock } from "./Skeleton";

/**
 * Tailored Leaderboard skeleton: top-3 podium + ranked rows.
 *
 * The page title, subtitle and period tabs are real chrome rendered by the
 * screen — only the board itself is grey.
 *
 * The medal border colours are intentionally NOT drawn here. Gold/silver/bronze
 * state a result, and on the period tabs the top three change; a neutral border
 * says "a card goes here" without asserting who won.
 *
 * Switching period (Weekly → All time) replaces the dataset entirely, so row
 * skeletons ARE correct on that switch — the documented exception to
 * "never go backwards".
 */

const PodiumCardSkeleton = ({ tall = false }) => (
  <div className={`relative ${tall ? "" : "sm:mt-5"}`}>
    <div
      className={`relative z-0 bg-white rounded-2xl px-2 py-4 sm:p-5 flex flex-col border border-gray-200 ${
        tall ? "py-5" : "shadow-sm"
      }`}
    >
      <div className="flex sm:items-start items-center gap-2 sm:gap-3 sm:flex-row flex-col">
        {/* Rank badge slot — the number itself is a real fact we don't have. */}
        <SkeletonBlock
          rounded="rounded-full"
          className="sm:w-8 w-7 sm:h-8 h-7 flex-shrink-0 sm:relative absolute sm:-left-2 -left-1 sm:-top-2 -top-1"
        />
        <SkeletonBlock
          rounded="rounded-full"
          className="w-14 h-14 sm:w-16 sm:h-16 shrink-0"
        />
        <div className="flex-1 min-w-0 w-full flex flex-col sm:items-start items-center gap-2">
          <SkeletonBlock height={14} width="70%" />
          {/* Performance figure. Never a "0.00%" here and never green or red. */}
          <SkeletonBlock height={18} width="45%" />
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-2">
        <SkeletonBlock height={12} width="60%" />
        <SkeletonBlock height={12} width="48%" />
      </div>
    </div>
  </div>
);

const RankedRowSkeleton = () => (
  <div>
    {/* Mobile: rank · avatar · handle · perf */}
    <div className="sm:hidden flex items-center gap-2 py-3 px-3">
      <SkeletonBlock height={12} width={14} className="flex-shrink-0" />
      <SkeletonBlock rounded="rounded-full" width={32} height={32} />
      <div className="flex-1 min-w-0">
        <SkeletonBlock height={12} width="55%" />
      </div>
      <SkeletonBlock height={12} width={52} className="flex-shrink-0" />
    </div>

    {/* Desktop: rank · avatar · handle · perf · fans · follow slot */}
    <div className="hidden sm:flex items-center gap-3 sm:gap-6 py-3 sm:py-4">
      <SkeletonBlock height={14} width={18} className="flex-shrink-0" />
      <SkeletonBlock
        rounded="rounded-full"
        className="w-10 h-10 sm:w-11 sm:h-11 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <SkeletonBlock height={14} width="55%" />
      </div>
      <div className="flex-1 min-w-[60px]">
        <SkeletonBlock height={14} width={64} />
      </div>
      <div className="flex-1 min-w-[100px]">
        <SkeletonBlock height={13} width={72} />
      </div>
      {/* The Follow column keeps its width so rows don't reflow, but stays
          empty — a grey block the size of a button invites a tap. */}
      <div className="flex-shrink-0 w-[140px]" />
    </div>
  </div>
);

const LeaderboardSkeleton = ({ rows = 7, showPodium = true }) => (
  <div aria-busy="true" aria-label="Leaderboard loading">
    {showPodium && (
      <div className="mb-8 mt-4 pt-8">
        <div className="grid grid-cols-3 gap-3 sm:gap-5 lg:gap-6">
          <PodiumCardSkeleton />
          <PodiumCardSkeleton tall />
          <PodiumCardSkeleton />
        </div>
      </div>
    )}

    {/* "You're ranked" card: chrome and copy are static, so they render for
        real; only the rank figure waits. Handled by the screen. */}

    <div className="bg-white divide-y divide-gray-200">
      {Array.from({ length: rows }).map((_, i) => (
        <RankedRowSkeleton key={i} />
      ))}
    </div>
  </div>
);

export default LeaderboardSkeleton;
